import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { Container, Paper, TextField, Button, Typography, Box, Alert, MenuItem, FormControl, InputLabel, Select } from '@mui/material';
import Header from './components/Header';
import { useAuth } from './context/AuthContext';

const Profile = () => {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [name, setName] = useState(user?.name || '');
  const [surname, setSurname] = useState(user?.surname || '');
  const [companyName, setCompanyName] = useState(user?.company_name || '');
  const [roleAtCompany, setRoleAtCompany] = useState(user?.role_at_company || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Check if coming from certificate download flow
  const fromCertificate = location.state?.fromCertificate;
  const courseId = location.state?.courseId;

  // Role options in alphabetical order as requested
  const roleOptions = [
    "Administrator for a Site",
    "Administrator for the Organisation",
    "Alert Monitor Agent",
    "Alert Monitor Team Leader",
    "Business Unit Manager",
    "Camera Technician",
    "Director",
    "Training Department"
  ];

  if (!user) {
    return (
      <Box sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0054A6 0%, #00386e 55%, #001f3f 100%)'
      }}>
        <Header />
        <Container sx={{ mt: 4 }}>
          <Alert severity="warning">Please sign in to manage your profile.</Alert>
        </Container>
      </Box>
    );
  }

  const handleSave = async () => {
    // Validation - all fields are required and must contain valid text
    if (!name?.trim()) {
      setError('First name is required');
      return;
    }
    if (!surname?.trim()) {
      setError('Surname is required');
      return;
    }
    if (!companyName?.trim()) {
      setError('Company name is required');
      return;
    }
    if (!roleAtCompany?.trim()) {
      setError('Role at company is required');
      return;
    }

    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const resp = await axios.put(`/api/users/${user.id}`, {
        name: name.trim(),
        surname: surname.trim(),
        company_name: companyName.trim(),
        role_at_company: roleAtCompany
      });
      // Update AuthContext + localStorage with new user
      login(resp.data);
      setSuccess('Your profile has been updated.');

      // If coming from certificate download, redirect back to dashboard with highlight
      if (fromCertificate && courseId) {
        setTimeout(() => {
          navigate(`/dashboard?profileUpdated=true&courseId=${courseId}`);
        }, 1500);
      }
    } catch (e) {
      console.error('Failed to update profile', e);
      setError(e?.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0054A6 0%, #00386e 55%, #001f3f 100%)'
    }}>
      <Header />
      <Container maxWidth="sm" sx={{ mt: 4 }}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h5" gutterBottom>My Profile</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Update your profile information. All fields are required. Your email is managed by Google Sign-In and cannot be changed here.
          </Typography>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

          <TextField
            label="Email"
            value={user.email}
            fullWidth
            margin="normal"
            disabled
          />

          <TextField
            label="First Name *"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            margin="normal"
            required
            error={!name?.trim()}
            helperText={!name?.trim() ? "First name is required" : ""}
          />

          <TextField
            label="Surname *"
            value={surname}
            onChange={(e) => setSurname(e.target.value)}
            fullWidth
            margin="normal"
            required
            error={!surname?.trim()}
            helperText={!surname?.trim() ? "Surname is required" : ""}
          />

          <TextField
            label="Company Name *"
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
            fullWidth
            margin="normal"
            required
            error={!companyName?.trim()}
            helperText={!companyName?.trim() ? "Company name is required" : ""}
          />

          <FormControl fullWidth margin="normal" required error={!roleAtCompany?.trim()}>
            <InputLabel id="role-select-label">Role at Company *</InputLabel>
            <Select
              labelId="role-select-label"
              value={roleAtCompany}
              label="Role at Company *"
              onChange={(e) => setRoleAtCompany(e.target.value)}
            >
              {roleOptions.map((role) => (
                <MenuItem key={role} value={role}>
                  {role}
                </MenuItem>
              ))}
            </Select>
            {!roleAtCompany?.trim() && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                Role at company is required
              </Typography>
            )}
          </FormControl>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Button variant="contained" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default Profile;
